import type { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { SeededRandom } from "../core/SeededRandom";
import { TileType, manhattanDistance, type LevelData, type Point } from "../generation/types";
import type { MaterialFactory } from "./MaterialFactory";
import { gridToWorld } from "./worldCoordinates";
import { applyBoxQuadUvs } from "./UvTiling";

export interface VentMount {
  readonly cell: Point;
  readonly direction: Point;
  readonly speed: number;
}

export function selectVentMounts(level: LevelData): VentMount[] {
  const random = new SeededRandom(`${level.generationSeed}:ventilation`);
  const mounts: VentMount[] = [];
  const directions = [
    { x: 1, y: 0 },
    { x: -1, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: -1 },
  ];
  for (const room of level.rooms) {
    if (room.id === "secret" || !random.chance(0.48)) continue;
    const candidates: Omit<VentMount, "speed">[] = [];
    for (let y = room.y; y < room.y + room.height; y++)
      for (let x = room.x; x < room.x + room.width; x++) {
        const cell = { x, y };
        if (
          level.grid.get(cell) !== TileType.Floor ||
          level.doors.some((door) => door.cells.some((p) => manhattanDistance(cell, p) < 4))
        )
          continue;
        for (const direction of directions) {
          if (level.grid.get({ x: x + direction.x, y: y + direction.y }) === TileType.Wall)
            candidates.push({ cell, direction });
        }
      }
    if (candidates.length)
      mounts.push({ ...random.pick(candidates), speed: random.range(1.7, 3.2) });
    if (mounts.length === 5) break;
  }
  return mounts;
}

export class VentilationSystem {
  private readonly rotors: Array<{ node: TransformNode; speed: number }> = [];
  public constructor(scene: Scene, level: LevelData, materials: MaterialFactory) {
    for (const [index, mount] of selectVentMounts(level).entries()) {
      const world = gridToWorld(mount.cell, level.grid);
      const root = new TransformNode(`vent-${index}`, scene);
      root.position.set(
        world.x + mount.direction.x * 0.91,
        2.1,
        world.z + mount.direction.y * 0.91,
      );
      root.rotation.y = Math.atan2(mount.direction.x, mount.direction.y);
      const housing = MeshBuilder.CreateBox(
        `vent-housing-${index}`,
        { width: 1.8, height: 1.8, depth: 0.12 },
        scene,
      );
      housing.parent = root;
      housing.material = materials.get("ventMetal");
      applyBoxQuadUvs(housing, { width: 1.8, height: 1.8, depth: 0.12 }, 1.8, 1.8);
      const rotor = new TransformNode(`vent-rotor-${index}`, scene);
      rotor.parent = root;
      rotor.position.z = -0.16;
      for (let blade = 0; blade < 4; blade++) {
        const angle = (blade * Math.PI) / 2;
        const mesh = MeshBuilder.CreateBox(
          `vent-blade-${index}-${blade}`,
          { width: 0.28, height: 0.67, depth: 0.045 },
          scene,
        );
        mesh.parent = rotor;
        mesh.position.set(Math.sin(angle) * 0.36, Math.cos(angle) * 0.36, 0);
        mesh.rotation.z = -angle + 0.22;
        mesh.material = materials.get("metalPanels");
      }
      const hub = MeshBuilder.CreateCylinder(
        `vent-hub-${index}`,
        { diameter: 0.25, height: 0.13, tessellation: 10 },
        scene,
      );
      hub.parent = rotor;
      hub.rotation.x = Math.PI / 2;
      hub.material = materials.get("rustedRivets");
      this.rotors.push({ node: rotor, speed: mount.speed });
    }
  }
  public update(deltaSeconds: number): void {
    for (const rotor of this.rotors)
      rotor.node.rotation.z = (rotor.node.rotation.z + deltaSeconds * rotor.speed) % (Math.PI * 2);
  }
}
